var __jsx = React.createElement;
import React, { useState, useMemo, useCallback, useRef } from "react";
import AwesomeIcon from "../../AwesomeIcon";
import { faSearch, faTimes } from "@fortawesome/pro-solid-svg-icons";
import GroupedFormSection from "./GroupedFormSection";
import Loader from "../../Loader";
import RequiredFieldsStatus from "./RequiredFieldsStatus";
import { calculateRequiredFieldsStatus } from "../../../utils/acrUtils";
import { FormSectionProvider } from "./FormSectionContext";
import useSubmissionFormData from "../hooks/useSubmissionFormData";
import { useFieldSearch } from "../hooks/useFieldSearch";
import { SUBMISSION_STATUS } from "../../../constants/acr";
import { useSharedFormData } from "./SharedFormDataContext";
import { useStoreState } from "pullstate";
import ACRStore from "../../../utils/ACRStore";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
var SubmissionFormContent = function SubmissionFormContent(_ref) {
  var searchQuery = _ref.searchQuery,
    setSearchQuery = _ref.setSearchQuery,
    submissionDetail = _ref.submissionDetail,
    fieldErrors = _ref.fieldErrors,
    fullFieldErrors = _ref.fullFieldErrors,
    fieldErrorsList = _ref.fieldErrorsList,
    _ref$isLoading = _ref.isLoading,
    isLoading = _ref$isLoading === void 0 ? false : _ref$isLoading,
    fieldSchemas = _ref.fieldSchemas,
    workspaceId = _ref.workspaceId;
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState(null),
    highlightedFieldName = _useState[0],
    setHighlightedFieldName = _useState[1];
  var _useState2 = useState(false),
    isDropdownOpen = _useState2[0],
    setIsDropdownOpen = _useState2[1];

  // Ref to store section expand API from GroupedFormSection
  var expandSectionRef = useRef(null);

  // Get formData and handleFieldChange from the hook
  var _useSubmissionFormDat = useSubmissionFormData(submissionDetail),
    formData = _useSubmissionFormDat.formData,
    handleFieldChange = _useSubmissionFormDat.handleFieldChange;

  // Get error lists from context (already filtered for stale errors by useErrorManager)
  var _useSharedFormData = useSharedFormData(),
    errorsForBadge = _useSharedFormData.errorsForBadge;

  // Get registry tagged fields from store (for special checks like "at least one required")
  var registryTaggedFields = useStoreState(ACRStore, function (s) {
    var _s$registryTaggedFiel;
    return ((_s$registryTaggedFiel = s.registryTaggedFields) === null || _s$registryTaggedFiel === void 0 ? void 0 : _s$registryTaggedFiel[workspaceId]) || {};
  }, [workspaceId]);

  // Calculate isSubmissionReadOnly
  var isSubmissionReadOnly = useMemo(function () {
    var _submissionDetail$sta;
    var status = submissionDetail === null || submissionDetail === void 0 || (_submissionDetail$sta = submissionDetail.status) === null || _submissionDetail$sta === void 0 ? void 0 : _submissionDetail$sta.toLowerCase();
    return status === SUBMISSION_STATUS.SUBMITTED || status === SUBMISSION_STATUS.ACCEPTED;
  }, [submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.status]);

  // Get filtered fields and error maps
  var _useFieldSearch = useFieldSearch({
      fieldSchemas: fieldSchemas,
      fieldErrorsList: fieldErrorsList,
      searchQuery: searchQuery
    }),
    filteredFields = _useFieldSearch.filteredFields,
    fieldErrorMap = _useFieldSearch.fieldErrorMap;

  // Calculate required empty fields using centralized function
  var requiredFieldsStatus = useMemo(function () {
    return calculateRequiredFieldsStatus(fieldSchemas, formData, fieldErrorsList, submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.missing_fields);
  }, [formData, fieldSchemas, fieldErrorsList, submissionDetail === null || submissionDetail === void 0 ? void 0 : submissionDetail.missing_fields]);
  var requiredEmptyFields = requiredFieldsStatus.emptyFields;
  var requiredEmptyCount = requiredFieldsStatus.empty;

  // Calculate fields with blocking errors for badge display
  // (excludes required markers, optional warnings, and stale backend errors)
  var fieldsWithBlockingErrorsData = useMemo(function () {
    // Build a set of field names that have blocking errors
    var fieldNamesWithErrors = new Set(errorsForBadge.map(function (error) {
      return error.field;
    }));

    // Filter fieldSchemas to get the full field objects
    var fieldsWithErrors = fieldSchemas.filter(function (field) {
      return fieldNamesWithErrors.has(field.field_name);
    });
    return {
      fields: fieldsWithErrors,
      count: fieldsWithErrors.length
    };
  }, [fieldSchemas, errorsForBadge]);
  var requiredFieldsWithErrors = fieldsWithBlockingErrorsData.fields;
  var fieldsWithErrorsCount = fieldsWithBlockingErrorsData.count;

  // Callback to handle highlight changes from RequiredFieldsStatus
  var handleHighlightChange = useCallback(function (fieldName) {
    setHighlightedFieldName(fieldName);
  }, []);

  // Callback to receive section expand API from GroupedFormSection
  var handleExpandSectionReady = useCallback(function (api) {
    expandSectionRef.current = api;
  }, []);

  // Create context value for FormSectionProvider
  var contextValue = useMemo(function () {
    return {
      filteredFields: filteredFields,
      fieldErrorMap: fieldErrorMap,
      fieldErrorsList: fieldErrorsList,
      fieldErrors: fieldErrors,
      submissionDetail: submissionDetail,
      formData: formData,
      handleFieldChange: handleFieldChange,
      setIsDropdownOpen: setIsDropdownOpen,
      isDropdownOpen: isDropdownOpen,
      isSubmissionReadOnly: isSubmissionReadOnly,
      highlightedFieldName: highlightedFieldName,
      registryTaggedFields: registryTaggedFields
    };
  }, [filteredFields, fieldErrorMap, fieldErrorsList, fieldErrors, submissionDetail, formData, handleFieldChange, isDropdownOpen, isSubmissionReadOnly, highlightedFieldName, registryTaggedFields]);
  return /*#__PURE__*/React.createElement("div", {
    className: "flex-1 min-w-0 min-h-0 flex flex-col bg-white dark:bg-gray-900"
  }, /*#__PURE__*/React.createElement("div", {
    className: "flex gap-2 justify-between items-center px-5 py-3 bg-[#E8E9EA] dark:bg-[#141F2A] border-b border-[#BBBFC2] dark:border-[#646B72] flex-shrink-0 space-y-4"
  }, /*#__PURE__*/React.createElement("div", {
    className: classNames("relative", {
      "min-w-[40%]": !isSubmissionReadOnly,
      "min-w-[50%]": isSubmissionReadOnly
    })
  }, /*#__PURE__*/React.createElement("input", {
    type: "text",
    placeholder: t("label.search_fields"),
    value: searchQuery,
    onChange: function onChange(e) {
      return setSearchQuery(e.target.value);
    },
    className: "w-full h-[36px] px-4 py-2 pl-7 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-900 text-gray-800 dark:text-white placeholder:text-base placeholder:text-gray-400 focus:outline-none focus:border-teal-500"
  }), /*#__PURE__*/React.createElement("div", {
    className: "absolute left-4 top-1/2 transform-none -translate-y-1/2 text-gray-500 pointer-events-none"
  }, /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faSearch,
    size: "sm"
  })), searchQuery && /*#__PURE__*/React.createElement("button", {
    type: "button",
    onClick: function onClick() {
      return setSearchQuery("");
    },
    className: "absolute right-4 top-1/2 transform-none -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none",
    "aria-label": t("label.clear_search")
  }, /*#__PURE__*/React.createElement(AwesomeIcon, {
    name: faTimes,
    size: "sm"
  }))), !isSubmissionReadOnly && /*#__PURE__*/React.createElement(RequiredFieldsStatus, {
    requiredEmptyFields: requiredEmptyFields,
    requiredEmptyCount: requiredEmptyCount,
    requiredFieldsWithErrors: requiredFieldsWithErrors,
    fieldsWithErrorsCount: fieldsWithErrorsCount,
    onHighlightChange: handleHighlightChange,
    expandSectionRef: expandSectionRef
  })), /*#__PURE__*/React.createElement("div", {
    className: "flex-1 overflow-hidden min-h-0 bg-[#F3F4F4] dark:bg-[#0C1723]"
  }, isLoading ? /*#__PURE__*/React.createElement("div", {
    className: "flex items-center justify-center h-full"
  }, /*#__PURE__*/React.createElement(Loader, {
    size: "large"
  })) : submissionDetail && /*#__PURE__*/React.createElement(FormSectionProvider, {
    value: contextValue
  }, /*#__PURE__*/React.createElement(GroupedFormSection, {
    searchQuery: searchQuery,
    onExpandSectionReady: handleExpandSectionReady
  }))));
};
export default SubmissionFormContent;